<script setup lang="ts">
import { useScrollRestoration, type ID } from '#shared'
import { computed, nextTick, onMounted, toRefs, useTemplateRef, watch } from 'vue'
import type { Message } from '#entities/message'
import { useScrollToSentMessage } from '../lib/use-scroll-to-sent-message'
import { useSmoothMessagesAppearing } from '../lib/use-smooth-messages-appearing'
import MessageListItem from './message-list-item.vue'

const props = defineProps<{
  messages: Message[]
  chatId: ID
  variant?: 'regular' | 'compact'
  scrollerEl: HTMLElement | null
  parentEl: HTMLElement | null
  targetMessageId: number
  navnonce: any
  areFreshMessagesDisplayed: boolean
}>()

const { messages, parentEl, scrollerEl, targetMessageId, navnonce, areFreshMessagesDisplayed } = toRefs(props)

// Messages counting from oldest to newest
const firstMessage = computed(() => messages.value.at(0))
const lastMessage = computed(() => messages.value.at(-1))

useScrollRestoration(scrollerEl, {
  listHead: lastMessage,
  listTail: firstMessage,
})

useSmoothMessagesAppearing(scrollerEl, lastMessage, areFreshMessagesDisplayed)
useScrollToSentMessage(scrollerEl, lastMessage)

const rootEl = useTemplateRef('rootEl')
function scrollToTargetMessage() {
  if (targetMessageId.value === -1) return

  const targetMessageEl = parentEl.value?.querySelector(
    `[data-message-id="${targetMessageId.value}"]`
  ) as HTMLElement | null

  const parentBox = parentEl.value?.getBoundingClientRect()
  const rootBox = rootEl.value?.getBoundingClientRect()
  const targetMessageBox = targetMessageEl?.getBoundingClientRect()

  if (!rootBox || !targetMessageBox || !parentBox) {
    return
  }

  const targetScrollTop = targetMessageBox.top - rootBox.top - parentBox.height / 2 + targetMessageBox.height / 2
  scrollerEl.value?.scrollTo({ top: targetScrollTop, behavior: 'instant' })
}

onMounted(() => {
  nextTick(() => scrollToTargetMessage())
})

watch(
  [navnonce],
  () => {
    nextTick(() => scrollToTargetMessage())
  },
  {
    immediate: true,
  }
)
</script>

<template>
  <div :class="$style.root" ref="rootEl">
    <MessageListItem
      v-for="message in messages"
      :key="message.id"
      :message="message"
      :chatId="chatId"
      :variant="variant"
      :isTarget="message.id === targetMessageId"
      :navnonce="navnonce"
    />
  </div>
</template>

<style lang="scss" module>
.root {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  min-height: 100%;
  padding-top: 20px;
}
</style>
